import CheckListForm from 'src/components/shared/checklist-form';
import { ChecklistCompletedOrNotCompletedConditionProviderProps, ChecklistCompletedOrNotCompletedConditionState } from './types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAutomationRuleConditions } from '../use-conditions';
import { ConditionItemType } from '../types';
import { useAzureProvider } from 'src/azure/context';
import { getFieldType } from 'src/azure/services/get-field';
import { getFields } from 'src/azure/services/work-items';

export default function ChecklistCompletedOrNotCompletedCondition(props: ChecklistCompletedOrNotCompletedConditionProviderProps) {
	const {conditions, setConditions, resetValidationErrors} = useAutomationRuleConditions();
	const {applicationContext} = useAzureProvider();

	const [state, setState] = useState<ChecklistCompletedOrNotCompletedConditionState>({
		options: [],
		initialOptions: [],
		loading: false,
		searchText: '',
		inputMode: 'selected',
	});

	const current = useMemo(() => {
		const found = conditions.find(item => item.id === props.conditionId);
		return found as ConditionItemType;
	}, [conditions, props.conditionId]);

	const findFieldOptions = useCallback(async () => {
		try {
			const fields = await getFields(applicationContext.projectId);
			if (fields) {
				return fields
					.filter(field =>
						getFieldType(field.type) === 'Html'
					)
					.map(field => ({
						label: field.name,
						value: field.referenceName
					}));
			}
	
			return [];
		} catch (error) {
			throw new Error('Error finding field options');
		}
	}, []);

	const changeState = useCallback((newState: Partial<ChecklistCompletedOrNotCompletedConditionState>) => {
		setState(oldState => ({
			...oldState,
			...newState,
		}));
	}, []);

	const changeSelectOptions = useCallback((value: string) => {
		setConditions(oldConditions => {
			const currentItem = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
			return [
				...oldConditions.filter(item => item.id !== props.conditionId),
				{...currentItem, checklistCompletedOrNotCompletedCondition: {field: value}}
			];
		});

		changeState({
			inputMode: 'selected',
			options: state.initialOptions,
			searchText: '',
		});
	
		resetValidationErrors();
	}, [state, props.conditionId]);

	const changeTextValue = useCallback((value: string) => {
		changeState({
			searchText: value,
			inputMode: 'searching',
			options: state.initialOptions.filter(item => item.label.toLowerCase().includes(value.toLowerCase())),
		});
	}, [state.initialOptions]);

	const renderValue = useMemo(() => {
		if (state.inputMode === 'selected') {
			return state.options.find(item => item.value === current.checklistCompletedOrNotCompletedCondition?.field)?.label || '';
		}

		return state.searchText;
	}, [
		state.inputMode,
		state.searchText,
		state.options,
		current.checklistCompletedOrNotCompletedCondition?.field
	]);

	useEffect(() => {
		changeState({loading: true});
		const asyncHandler = async () => {
			const options = await findFieldOptions();
			changeState({options, initialOptions: options, loading: false});
		};

		asyncHandler();
	}, []);

	return (
		<CheckListForm
			validation={props.validations?.field || ''}
			value={renderValue}
			changeSelectOptions={changeSelectOptions}
			changeTextValue={changeTextValue}
			options={state.options}
			loading={state.loading}
			selectedOptions={[]}
		/>
	);
}